import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/endpoints';
import { clearToken, getToken, setToken, UNAUTHORIZED_EVENT } from '../api/client';
import type { LoginResult, Staff } from '../api/types';

type AuthValue = {
  staff: Staff | null;
  restoring: boolean;
  isAdmin: boolean;
  /** Store the token from an approved QR sign-in. */
  login: (result: LoginResult) => void;
  logout: () => void;
  /** Replace the signed-in row after the account changes its own details. */
  setProfile: (profile: Staff) => void;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [restoring, setRestoring] = useState(true);

  const logout = useCallback(() => {
    clearToken();
    setStaff(null);
  }, []);

  // A stored token survives a refresh, but it may have expired or the account
  // may have been deactivated since — /auth/me is the cheap way to find out.
  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      if (!getToken()) {
        setRestoring(false);
        return;
      }
      try {
        const profile = await api.auth.me();
        if (!cancelled) setStaff(profile);
      } catch {
        if (!cancelled) clearToken();
      } finally {
        if (!cancelled) setRestoring(false);
      }
    };

    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  // Any request, including a background poll, can come back 401 once the 12h
  // token lapses. The client fires this event and we drop straight to login.
  useEffect(() => {
    window.addEventListener(UNAUTHORIZED_EVENT, logout);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, logout);
  }, [logout]);

  const login = useCallback((result: LoginResult) => {
    setToken(result.token);
    setStaff({ ...result.staff, is_active: true });
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      staff,
      restoring,
      isAdmin: staff?.role === 'admin',
      login,
      logout,
      setProfile: setStaff,
    }),
    [staff, restoring, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
